/**
 * Every user-facing string for the p3 Clean/Match/Send CRM flow, in one
 * object — renamable in one place (Dave's stage-name note). Views (Clean,
 * Match, Send) import from here rather than inlining literals so copy stays
 * a single source of truth.
 */
export const CRM_COPY = {
  stage: {
    /** BatchScreen's fourth Segmented option — today's plain leads table. */
    leads: 'Leads',
    clean: 'Clean',
    match: 'Match',
    send: 'Send',
  },
  /** Clean view's section header, prefixed to the row count. */
  needsEmailHeader: 'No email',
  /** Clean view's row message for a person with no email on file yet. */
  needsEmailRow: 'No email yet, so we can’t check your CRM',
  /** Clean view's empty state once every row has an email. */
  cleanEmptyState: 'Every lead here has an email. Open the Match tab next.',
  /** Clean view's inline-fix input placeholder. */
  addEmailPlaceholder: 'Add email…',
  /** Clean view's per-row save button label. */
  saveButton: 'Save',
  /**
   * Clean view's save-failure message. Deliberately generic (G6 plainness) —
   * never renders the raw error/exception text to the operator, in the toast
   * or inline; this is the single copy of that literal for both surfaces.
   */
  saveEmailFailed: 'Couldn’t save the email. Try again.',
  /**
   * Clean view's inline note under a non-empty draft that fails the email
   * shape gate. The gate mirrors the CRM engine's `normalizeEmail` (see
   * `EMAIL_SHAPE` in `CleanView.tsx`), so this fires at edit time for exactly
   * the values the engine would refuse after a successful save.
   */
  emailShapeInvalid: 'Doesn’t look like a valid email address',
  /**
   * Clean view's per-row note after a successful save while the row is still
   * present in `results` (the parent's refetch hasn't dropped it yet). Row
   * controls stay disabled for the duration so a slow refetch can't produce
   * a duplicate submit.
   */
  savedPendingRefresh: 'Saved. Checking your CRM…',
  /**
   * Clean view's per-row note when a COMPLETED refetch still lists the row as
   * needs_email after a successful save — the stored value didn't survive the
   * engine's stricter check, so the row reopens for another fix instead of
   * holding `savedPendingRefresh` forever.
   */
  savedStillNeedsEmail: 'Saved, but we still can’t check your CRM with this email. Try another.',
  /** Match view's group header for bucket `new`. */
  newHeader: 'New',
  /** Match view's empty state inside the `newHeader` column. */
  newEmptyState: 'Nobody new. Everyone here is already in your CRM.',
  /** Match view's group header for buckets `existing` + `ambiguous`. */
  alreadyInCrm: 'Already in your CRM',
  /** Match view's empty state inside the `alreadyInCrm` column. */
  alreadyInCrmEmptyState: 'Nobody here is in your CRM yet.',
  /**
   * Match view's above-the-columns disclosure (p3 Task 5 — spec 4 §5a).
   * Shown only when `summary.collapsed > 0`: the queue opens on the delta
   * view (`scope: 'changed'`), which hides rows already sent with nothing
   * new since. The brief's line names a "{date}", but nothing on the wire
   * carries a last-send timestamp to put there honestly — no field on
   * `PreviewSummary` or `PersonPreview` reports one — so this states the
   * count and leaves the date out rather than inventing one from the client
   * clock. `showAllLink` is a SEPARATE control composed beside the sentence,
   * not baked into it, so its accessible name is exactly "Show them" and not
   * the whole line; dispatches `{ type: 'showAllPreview' }`, a one-way
   * re-request under `scope: 'all'` (the backend decides membership, not a
   * client-side filter over what 'changed' already returned).
   */
  collapsedLine: (n: number): string =>
    `${n} ${n === 1 ? 'lead was' : 'leads were'} already sent, with no changed details and no new notes or follow-ups since.`,
  showAllLink: 'Show them',
  /** Shown on `ambiguous` rows in place of any decision controls. */
  ambiguousNote: 'Several CRM records share this email, so nothing will be changed.',
  /**
   * Match's "already in your CRM" column, on an `existing` row whose
   * `currentOwner` is set but `ownerRequired` is nonetheless true — the
   * archived-owned case (2026-10-02 follow-up): the owner on the record
   * can't act on it, either because it is the integration account or
   * because that CRM user has since been deactivated. Plain secondary text
   * under the picker, not a `HelpHint` — unlike the warning badge's
   * provenance-specific explanation, this just names what's wrong with the
   * owner already sitting on the record before the operator picks a new
   * one.
   *
   * Takes the name itself rather than reading `p.currentOwner.name` at the
   * call site — `OwnerPicker` never renders `currentOwner` while
   * `ownerRequired` is true, so this is the one place that name surfaces at
   * all, and the caller shouldn't have to know the blank-name fallback is
   * needed. Blank (HubSpot returns no name for some archived ids) falls
   * back to a nameless phrasing rather than rendering "  can’t follow up".
   */
  currentOwnerUnusableNote: (name: string): string => {
    const trimmed = name.trim();
    return trimmed
      ? `${trimmed} can’t follow up (deactivated, or the account that connects your CRM).`
      : 'The current owner can’t follow up (deactivated, or the account that connects your CRM).';
  },
  /** Badge on an owner suggestion the operator hasn't overridden. */
  suggestedBadge: 'Suggested',
  /** Same badge, specific to an `'assigned_to'` suggestion (spec §5.2, §8
   *  step 3) — the lead's own "Assigned to" column named this owner, which
   *  is worth calling out by name rather than folding into the generic
   *  "Suggested" wording used for every other provenance. */
  suggestedFromAssignedToBadge: 'Suggested from Assigned to',
  /** Green tag on every New row — `isReady` no longer waits on an owner (the
   *  backend's fallback chain always resolves one), so this is the same
   *  predicate Send uses, unconditional for `new`/`existing`, and the two
   *  tabs can never disagree (gh-31 D10). */
  readyToSendTag: 'Ready to send',
  /**
   * Muted tag next to `needsEmailRow` — the visible enrichment seam.
   *
   * "Waiting for email" was retired (spec 1 §5): it framed the lead as HELD
   * — waiting on something before it can go anywhere — directly under a hint
   * that now says "We still send these to your CRM". Nothing on Clean may
   * imply a block, because there is no longer one: an emailless lead reaches
   * Match and Send like any other, matched by the VendoIQ id property
   * instead of by email. This names what is missing and stops there.
   */
  enrichSeam: 'No email',
  /**
   * Match view's info note on a `new` row whose match-check found a similar
   * existing name (`p.similar`) — informational only, never auto-merged.
   * Composed as `${similarNotePrefix} ${p.similar} ${similarNoteSuffix}`.
   */
  similarNotePrefix: 'Similar name at',
  similarNoteSuffix: 'in your CRM. Sending still creates a new record.',
  /** Toggle button on an `existing` row that opens its Details drawer. */
  detailsToggle: 'Details',
  /** The Details drawer's two-column conflict header labels. */
  crmSaysLabel: 'CRM says',
  weSayLabel: 'We say',
  /**
   * The Details drawer's note on an UNPICKED conflict while the effective
   * merge policy is `overwrite`. The pre-selected side is then "We say"
   * because that IS the commit-time outcome — this line says so in plain
   * words and names the way out (an explicit keep-CRM pick, which the
   * backend honors above any policy). Embeds `detailsKeepCrm`'s wording
   * deliberately: it points at that control.
   */
  overwriteDefaultNote:
    'This replaces your CRM’s value, because “When a field differs” is set to “Replace it with ours”. Choose “Keep CRM” to keep your CRM’s value instead.',
  /** Details drawer, same lock. */
  detailsLockedNote: 'Locked by your company’s settings: CRM values are kept.',
  /** The Details drawer's muted line for a `not_patchable` decision —
   *  composed as `${notPatchablePrefix} ${fieldLabel}`. */
  notPatchablePrefix: 'Can’t update',
  /** Details drawer (gh-31 §4.9) — the side-by-side view of one person. */
  detailsFieldHeader: 'Field',
  detailsOutcomeHeader: 'What will happen',
  detailsKeepCrm: 'Keep CRM',
  detailsUseOurs: 'Use ours',
  detailsWillAdd: 'Will add',
  detailsSame: 'Same',
  detailsNothingToAdd: 'Nothing to add',
  detailsEmptyState: 'Nothing to compare. Your CRM already has everything we captured.',
  detailsCancel: 'Cancel',
  /** Details drawer's in-place save failure — generic by design (G6). */
  saveFieldFailed: 'Couldn’t save. Try again.',
  /** Friendly labels for `FieldDecision.field` canonical keys, used by the Details drawer. */
  fieldLabel: {
    first_name: 'First name',
    last_name: 'Last name',
    company: 'Company',
    title: 'Title',
    email: 'Email',
    phone: 'Phone',
    lead_source: 'Lead source',
    description: 'Description',
  },
  /** Match view's Lead/Contact tag text for an `existing` row's `remoteType`. */
  remoteTypeLabel: {
    lead: 'Lead',
    contact: 'Contact',
  },
  /** OwnerPicker's antd Select placeholder when no owner is chosen or suggested. */
  ownerPickerPlaceholder: 'Choose an owner…',
  /**
   * The Leads tab's bulk-assign control (`BulkActionsBar`) — VendoIQ's OWN
   * `persons.assigned_to`, the free-text "Assigned to" the worksheet column
   * shows. Deliberately NOT shared with Match's owner toolbar any more: see
   * `setCrmOwnerForSelected` below.
   */
  assignSelectedTo: 'Assign selected to…',
  /**
   * Match view's bulk owner toolbar label (spec 1 §6). Its own string, not
   * `assignSelectedTo` above, because the two controls write DIFFERENT
   * objects: this one sets the record owner in the customer's CRM, that one
   * sets `persons.assigned_to`, which is internal to VendoIQ and never
   * leaves it. One label over both taught the operator that "assign" meant
   * one thing when it meant two — spec 1 §6 requires this control to name
   * the CRM explicitly, and §9 requires the two controls no longer share a
   * label. The worksheet column keeps "Assigned to": that IS what it holds.
   */
  setCrmOwnerForSelected: 'Set CRM owner for selected…',
  /** Send view's ready-checklist section header, prefixed to `(selected/ready)`. */
  readySectionHeader: 'Ready to send',
  /**
   * Send's select-all control. Deliberately one label for both directions:
   * the box carries the state (checked / indeterminate / empty), so a label
   * that flipped between "Select all" and "Deselect all" would say the
   * opposite of what the box shows and re-word itself under the cursor.
   */
  selectAllReady: 'Select all',
  /** Send view's not-ready section header, prefixed to the row count. */
  notReadySectionHeader: 'Not ready yet',
  /** Send view's unready-row reason: bucket is `needs_email`. */
  reasonNoEmailYet: 'No email yet',
  /** Send view's unready-row reason: bucket is `ambiguous`. */
  reasonMultipleCrmRecords: 'Multiple CRM records',
  /**
   * The per-run merge policy — the control at the top of Match (gh-31 §4.7).
   * Each option's label is its radio's accessible name; the `*Detail` line
   * sits under it in secondary text.
   */
  policyGroupLabel: 'When a field differs',
  policyFillBlanks: 'Keep the CRM value (recommended)',
  policyFillBlanksDetail: 'We fill in fields your CRM has blank. We don’t change a value it already holds, unless you choose ours for a single lead.',
  policyOverwrite: 'Replace it with ours',
  policyOverwriteDetail:
    'Where the two differ, what we captured at the event replaces the CRM value. A value you choose for a single lead still wins.',
  /** MergePolicyControl, when the company's settings forbid overwriting (spec 2026-09-28 §5.8). */
  policyLockedNote: 'Your company’s settings keep CRM values, so we only fill in blank fields.',
  /** Send view's read-only restatement of the policy chosen on Match —
   *  composed as `${policyOnSendPrefix} ${policy label}. ${policyOnSendSuffix}`. */
  policyOnSendPrefix: 'When a field differs:',
  policyOnSendSuffix: 'Set on the Match tab.',
  /**
   * The stage strip's source control. `CRM`, deliberately not "Sending to" —
   * the control rides every stage tab and Clean/Match send nothing, so a
   * "Sending to" label would promise an action two tabs early.
   */
  sourceControlLabel: 'CRM',
  /** Same control with one connected source (or no manage rights): plain text. */
  sourceControlReadOnly: (source: string): string => `CRM: ${source}`,
  /**
   * Switch confirm, shown ONLY when the switch would discard work
   * (`hasQueueWork`). Names what goes, because the reset is invisible
   * otherwise, and reassures about what does not: switching re-reads the other
   * CRM, it never touches a lead.
   */
  switchSourceTitle: (next: string): string => `Switch to ${next}?`,
  switchSourceParts: (work: {
    ownerPicks: number;
    fieldChoices: number;
    deselected: number;
    policyChanged: boolean;
  }): string[] => {
    const parts: string[] = [];
    if (work.ownerPicks > 0)
      parts.push(`${work.ownerPicks} owner pick${work.ownerPicks === 1 ? '' : 's'}`);
    if (work.fieldChoices > 0)
      parts.push(`${work.fieldChoices} field choice${work.fieldChoices === 1 ? '' : 's'}`);
    if (work.deselected > 0) parts.push('your selection');
    if (work.policyChanged) parts.push('your “When a field differs” choice');
    return parts;
  },
  switchSourceBody: (next: string, parts: string[]): string => {
    const list =
      parts.length <= 1
        ? (parts[0] ?? '')
        : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    return `Switching to ${next} clears ${list}. Your leads aren’t affected.`;
  },
  switchSourceOk: 'Switch',
  switchSourceCancel: 'Cancel',
  /** Send view's summary sentence — composed as `${nCreate} ${summaryCreateSuffix}`. */
  summaryCreateSuffix: 'new records will be created',
  /** Send view's summary sentence — composed as `${nFill} ${summaryFillSuffix}`. */
  summaryFillSuffix: 'existing records get missing details',
  /**
   * Send view's summary sentence, shown only when the effective policy is
   * `overwrite` — composed as `${nOverwrite} ${summaryOverwriteSuffix}`, and
   * counting selected existing rows that still have an unpicked conflict.
   */
  summaryOverwriteSuffix: 'get CRM values replaced',
  /** Send view's summary sentence — composed as `${nChoices} ${summaryChoicesSuffix}`. */
  summaryChoicesSuffix: 'keep your per-field choices',
  /** Send view's summary sentence — composed as `${nSuggested} ${summarySuggestedSuffix}`. */
  summarySuggestedSuffix: 'owners auto-suggested',
  /**
   * Send view's above-the-button disclosures (p3 Task 4 — spec 1 §3 step 5
   * "reported, never silent" / §4 resolution (b)). All four are
   * informational only; none may disable Send. Each is a whole sentence
   * with its count already baked in (unlike the `summary*Suffix` lines
   * above, which are appended to a shared template) because the four
   * shapes differ too much to share one.
   */
  sendDisclosures: {
    /**
     * `summary.needsEmail > 0`. Plain words, no implication that this is
     * something the operator must fix first — spec 1 §4 chose disclosure
     * over a gate on purpose, and Clean already lets these leads through.
     */
    needsEmail: (n: number): string =>
      `${n} ${n === 1 ? 'lead has' : 'leads have'} no email, so we couldn’t check by email whether ${n === 1 ? 'it’s' : 'they’re'} already in your CRM.`,
    /**
     * `summary.integrationAccountOwned > 0`. Names the count and says
     * plainly what it means: nobody is following these up.
     *
     * Deliberately NOT "will be owned by the integration account" — that
     * mechanism claim is only true for a row with no owner at all (or one
     * already owned by the integration account). A row counted here can
     * also be an `existing` row whose CURRENT owner has been deactivated in
     * the CRM: `ownerRequired` is true for it, but `commitBodyFrom` sends no
     * `ownerId` when nothing resolved one, so an ownerless row stays
     * ownerless and an archived-owned row stays archived-owned — neither
     * necessarily lands on the integration account. Rather than enumerate
     * every outcome, this keeps to the one thing true of all of them:
     * nobody will follow up.
     */
    integrationAccountOwned: (n: number): string =>
      `${n} ${n === 1 ? 'lead has' : 'leads have'} no owner in your CRM who can follow ${n === 1 ? 'it' : 'them'} up.`,
    /**
     * `summary.ownerUnverified > 0` — a row-level flag (`PersonPreview.
     * ownerUnverified`), counted here.
     */
    ownerUnverified: (n: number): string =>
      `${n} ${n === 1 ? 'lead uses' : 'leads use'} your default owner, but we couldn’t confirm that person is an active user in your CRM.`,
    /**
     * `summary.ownersUnavailable === true` — a boolean, not a count, and
     * deliberately NOT phrased like `integrationAccountOwned` above: that
     * one states a fact about the rows it counts ("no usable owner"),
     * confirmed by the preview. This one is a confession of ignorance — "we
     * couldn't read your CRM's user list, so we don't know who these will
     * go to" — which the degraded lookup makes true of EVERY row regardless
     * of its actual owner. Given its own visual treatment in `SendView` (an
     * Alert, not a plain line) so it reads as "the data is untrustworthy"
     * rather than as a fourth fact alongside the other three.
     */
    ownersUnavailable:
      'We couldn’t read your CRM’s list of users, so the owners shown here may be wrong. A lead can show an owner warning even when its owner is fine.',
  },
  /** Send view's commit button label. */
  sendButton: 'Send',
  /**
   * Activity-timeline lines. "Timeline" is the CRM's own framing for a
   * record's logged calls and tasks, but the counts are phrased in what
   * VendoIQ captured — conversations and follow-ups — not in CRM object
   * names the operator never chose.
   */
  activity: {
    /** Match row: what this send will add. Never rendered with both at zero. */
    willLog: (calls: number, tasks: number): string => {
      const parts: string[] = [];
      if (calls > 0) parts.push(`${calls} ${calls === 1 ? 'conversation' : 'conversations'}`);
      if (tasks > 0) parts.push(`${tasks} ${tasks === 1 ? 'follow-up' : 'follow-ups'}`);
      return `Will add ${parts.join(' and ')} to the CRM timeline`;
    },
    /** Send result sub-line, per person. */
    logged: (n: number): string => `${n} added to the CRM timeline`,
    /** Shown only alongside a landed person write — the lead itself still stood. */
    failed: (n: number): string =>
      `${n} couldn’t be added. We’ll try again on the next send.`,
  },
  /** Send view's results panel header, prefixed to the row count. */
  resultsHeader: 'Results',
  /** Send view's per-row action on a `changed_since_preview` result — re-runs the parent's forced recheck via `onCommitted`. */
  recheckButton: 'Recheck',
  /**
   * Send view's way back from the results to the two panes. The results
   * replace the panes, so without this an operator who sent a first batch
   * could not reach the rows they deliberately held back until the view was
   * remounted. It clears the local `outcome` and nothing else — the rows that
   * went out were already spent by `resetAfterSend`, so what comes back is
   * exactly the selection the operator left behind.
   */
  backToListButton: 'Back to the list',
  /**
   * Send view's per-row outcome tag text, keyed by `CommitResultRow.outcome`.
   * The KEYS are wire values from the backend's `CommitResultRow.outcome`
   * union and must never change; only the labels are ours. `membership_only`
   * and `changed_since_preview` read as internal vocabulary (the
   * campaign_members ledger; a "preview" step the operator is never shown
   * named), so their labels say what happened instead.
   */
  outcomeLabel: {
    created: 'Created',
    updated: 'Updated',
    membership_only: 'Added to the event',
    changed_since_preview: 'Changed in your CRM',
    needs_email: 'Needs email',
    ambiguous: 'Multiple CRM records',
    failed: 'Failed',
    incomplete: 'Held back: notes too short',
  },
  /** Send results: an `incomplete` row the rules held for missing fields (spec 2026-09-28 §6). */
  incompleteHeldLabel: 'Held back: missing required fields',
  /** Send results: the event-link outcomes worth a line (spec 2026-09-28 §5.9). */
  eventLinkNote: {
    kept_existing: 'Kept the event it was already linked to in Salesforce',
    not_linked: 'Contact not linked to the event (your Salesforce settings link Leads only)',
  } as Partial<Record<'linked' | 'kept_existing' | 'contact_role_added' | 'not_linked', string>>,
  /** Send results: Tasks fell through the scanner rule (spec 2026-09-28 D9). */
  taskOwnerFallback: 'Tasks went to the account that connects Salesforce, because no Assigned to name, scanning rep or default owner matched.',
  /** Send view's success toast — `summary.failed === 0`. */
  sendSuccessToast: 'Sent to your CRM.',
  /** Send view's partial-failure toast — `summary.failed > 0`. */
  sendPartialFailureToast: 'Sent to your CRM, but not every lead went through. Check the results.',
  /**
   * Send view's toast for a rejected `api.commit(...)` call (network/server
   * error, not a per-row failure) — every such failure EXCEPT a 409.
   * Deliberately generic (G6 plainness, same convention as
   * `saveEmailFailed`) — never renders the raw error text. A 409 is a stop
   * someone can act on (spec 2026-09-28 §7 — several Salesforce Opportunities
   * match this event, say, or another Opportunity-container refusal), so it
   * shows the server's own message instead of this toast.
   */
  sendFailedToast: 'Couldn’t send to your CRM. Try again.',
  /**
   * CrmViews' error line when the match-check query fails. Generic by design
   * (G6 plainness, same convention as `saveEmailFailed`) — the raw error text
   * never reaches the operator.
   */
  matchCheckFailedLine: 'Couldn’t check these leads against your CRM. Press Retry to try again.',
  /** CrmViews' error line when the preview query fails (Clean stays usable). */
  previewFailedLine: 'Couldn’t look up these leads in your CRM. Press Retry to try again.',
  /**
   * Shown wherever Match or Send would otherwise render an empty state while
   * the live CRM read those two tabs trigger is still in flight — and as the
   * reason Send is unavailable for the same moment. Says "looking up" rather
   * than "checking" so it can't be mistaken for the cheap opening check
   * `matchCheckFailedLine` names.
   */
  previewLoadingLine: 'Looking up these leads in your CRM…',
  /** CrmViews' retry control on either error line. */
  retryButton: 'Retry',
  /** That control's accessible name on `matchCheckFailedLine`. */
  retryMatchCheckLabel: 'Retry checking these leads against your CRM',
  /** That control's accessible name on `previewFailedLine`. */
  retryPreviewLabel: 'Retry looking up these leads in your CRM',
  /** CrmViews' manual re-check control, next to the stage Segmented. */
  refreshButton: 'Refresh',
  /**
   * Bulk actions on the Leads tab's restored row selection (customer portal).
   * Not part of the CRM flow, but this module is the Batch screen's single
   * copy source of truth, so the strings live here rather than inline.
   *
   * Sentences are COMPOSED from these parts at the call site, the same way
   * `similarNotePrefix`/`similarNoteSuffix` are — see `BulkActionsBar`.
   */
  bulk: {
    /** The bar's leading count — composed as `${n} ${selectedSuffix}`. */
    selectedSuffix: 'selected',
    /** Opens the add/remove tag popover. The assign control beside it uses
     *  `assignSelectedTo` above — the VendoIQ-internal "Assigned to", which
     *  is this bar's only assignment vocabulary. Match's CRM-owner toolbar
     *  deliberately says something else (`setCrmOwnerForSelected`): the two
     *  write different objects and must not read as one action (spec 1 §6). */
    tagButton: 'Tag selected…',
    deleteButton: 'Delete selected',
    /** Deleted tab only — the one action a removed lead accepts. */
    restoreButton: 'Restore selected',
    clearButton: 'Clear selection',
    /** Tag popover's add/remove choice. */
    tagAdd: 'Add',
    tagRemove: 'Remove',
    /** Tag popover's picker placeholder + accessible name. */
    tagPlaceholder: 'Choose a tag',
    /** The VIP entry in the tag catalog (Status/Custom entries are data). */
    vipOption: 'VIP',
    /** Both popovers' confirm control. */
    applyButton: 'Apply',
    /** Assign popover's input placeholder. */
    assignPlaceholder: 'Name or email',
    assignClearNote: 'Leave blank to clear the assignment.',
    deleteConfirmTitle: 'Delete the selected leads?',
    /** Confirm body — composed as `${prefix} ${n} ${leadWord}. ${suffix}`. */
    deleteConfirmPrefix: 'This deletes',
    /** Deleting is VendoIQ-only (a soft archive): nothing is removed from the
     *  CRM, which an operator tidying up after a send may otherwise assume. */
    deleteConfirmSuffix: 'They move to the Deleted tab, where they can be restored. Leads you’ve already sent stay in your CRM.',
    deleteConfirmOk: 'Delete',
    leadSingular: 'lead',
    leadPlural: 'leads',
    /** Result toast — `${verb} ${ok} ${resultOf} ${total} ${leadWord}`, with
     *  `. ${n} ${resultFailedSuffix}.` appended when some leads failed. */
    resultOf: 'of',
    taggedVerb: 'Tagged',
    untaggedVerb: 'Untagged',
    assignedVerb: 'Assigned',
    deletedVerb: 'Deleted',
    restoredVerb: 'Restored',
    resultFailedSuffix: 'failed',
    /**
     * Every lead failed. Generic by design (G6 plainness, same convention as
     * `saveEmailFailed`) — the raw error text never reaches the operator.
     */
    allFailed: 'Nothing changed. Try again.',
    /**
     * Live progress while a run is in flight, in the bar's leading slot in
     * place of the selection count — composed as
     * `${done} ${resultOf} ${total} ${leadWord} ${progressSuffix}`.
     */
    progressSuffix: 'done…',
    /**
     * Shown beside that progress on a selection big enough to outrun the
     * server's write budget (`SERVER_WRITE_BURST`), where the runner slows
     * itself to about a lead a second on purpose. Without it, a bar that
     * advances once a second reads as a hang.
     */
    pacedNote: 'Large selections run at about one lead a second, so none are missed.',
  },
  /**
   * Event rail — the count on the selected event (gh-31 §4.4, reworded p3
   * Task 8). It is "what's worth a look", not "what blocks Send" — neither
   * term gates sending any more (spec 1 §5's Clean is informational; spec 1
   * §3's owner fallback chain always resolves one), so the copy must never
   * imply either does. It is Clean's needs-email rows plus the rows with no
   * owner who can follow up (integration-account-owned, ownerless, or
   * archived-owned) or carrying an unverified configured default
   * (`unresolvedOwnerCount`, mirroring `OwnerPicker`'s D9 rule). It is NOT
   * the event's scan count (that is the Total Scans tile),
   * and it only ever shows on the selected event. `n` is the badge's own
   * number, so the sentence can never disagree with it. Phrased with "or"
   * between the two reason clusters (never "and") so it still reads
   * correctly when only one of the two terms contributed to `n`.
   */
  rail: {
    badgeTooltip: (n: number): string =>
      `${n} ${n === 1 ? 'lead is' : 'leads are'} worth a look: no email to check against your CRM, no owner who can follow up, or a default owner we couldn’t confirm.`,
  },
  /**
   * The explanatory layer (issue 7): what each tab does, and what each item
   * inside it means. Rendered by `HelpHint.tsx`'s three affordances —
   * `HintLine` (always visible), `HelpHint` (a "?" beside a small inline
   * object) and `HelpPopover` (a list, for the three places where the honest
   * answer is three or more cases).
   *
   * Every entry carries a `label` — the accessible name of its trigger — even
   * where the entry renders as an always-visible line and nothing triggers it,
   * so the block has one shape throughout. `text` is a single sentence or two;
   * `title` + `lines` is the list form.
   */
  help: {
    /** About writes, never "touches": the opening check described in
     *  `refresh` below runs on this tab too. And no longer "nothing you do
     *  here changes your CRM": inline edits and notes on this tab save to the
     *  lead at once and go out on the next Send (an already-sent lead comes
     *  back on Send as changed), so the line says when, not that they don't. */
    tabLeads: {
      label: 'What the Leads tab is',
      text: 'Every lead captured at this event. Your edits save here right away. Nothing on this screen is written to your CRM until you press Send. The Match tab shows what a send will change. Tags stay in VendoIQ.',
    },
    /** Spec 1 §5 — Clean is enrichment, not a gate: these leads already reach
     *  Match and can already be sent (matched by VendoIQ id rather than
     *  email), so the text says what adding an email improves rather than
     *  implying the lead is stuck here until it's added. */
    tabClean: {
      label: 'What the Clean tab is',
      text: 'Leads with no email address. We can’t check first whether they’re already in your CRM. They can still be sent, unless your lead rules hold them back. Add an email to fix that, or leave it for now.',
    },
    /** Opening this tab is what triggers the live CRM read, so the hint leads
     *  with that rather than describing a look-up that has already happened —
     *  on a cold draw it hasn't. Reworded alongside `refresh` below. */
    tabMatch: {
      label: 'What the Match tab is',
      text: 'Shows how your leads compare with your CRM: who’s new, who’s already there, and which fields differ. Opening this tab looks up your leads in your CRM, which takes a moment the first time. Leads already sent with nothing new aren’t looked up again. Nothing is sent from this tab. You only make choices here.',
    },
    /** Send triggers the same live read as Match, so it says so too — an
     *  operator who only ever opens Send never reads the Match hint. */
    tabSend: {
      label: 'What the Send tab is',
      text: 'The last step. Opening it looks up your leads in your CRM, the same as Match. Leads that are ready are already ticked. Untick any you want to hold back, then press Send. Nothing is written to your CRM until you do.',
    },
    cleanQueue: {
      label: 'Why these leads are here',
      text: 'We look leads up in your CRM by email address. These leads have no email, so we can’t check first whether they’re already there. They can still be sent, unless your lead rules hold them back.',
    },
    cleanWaiting: {
      label: 'What the “No email” tag means',
      text: 'Nothing has gone wrong. This lead has no email address yet, so we can’t check first whether it’s already in your CRM. It can still be sent, unless your lead rules hold it back. Add an email to this lead, or leave it for now.',
    },
    cleanSaveEmail: {
      label: 'What happens when I save an email',
      text: 'Type the person’s work email and press Save. We check your CRM for them right away, and the lead moves off this tab on its own.',
    },
    cleanSavedStillNeedsEmail: {
      label: 'Why the email didn’t work',
      text: 'The address saved, but we still can’t look it up in your CRM. This is usually a typo, or an address your CRM won’t accept. Try the person’s work email.',
    },
    matchNewGroup: {
      label: 'What “New” means',
      text: 'We didn’t find these people in your CRM. Sending creates a new record for each of them.',
    },
    matchAlreadyGroup: {
      label: 'What “Already in your CRM” means',
      text: 'We found these people in your CRM, so we won’t create a second record for them. We only fill in what’s missing, unless you choose to replace values.',
    },
    matchLeadTag: {
      label: 'What the “Lead” tag means',
      text: 'Your CRM holds this person as a Lead. We’ll update that record rather than create a second one.',
    },
    matchContactTag: {
      label: 'What the “Contact” tag means',
      text: 'Your CRM holds this person as a Contact. We’ll update that record rather than create a second one.',
    },
    matchSimilar: {
      label: 'Why this lead shows a similar name',
      text: 'Someone with the same name at the same company is already in your CRM, but not under this email, so it might not be the same person. We don’t merge on a guess, so sending creates a new record. If it is the same person, fix the email in your CRM, then press Refresh.',
    },
    matchAmbiguous: {
      label: 'What to do about multiple CRM records',
      text: 'More than one record in your CRM uses this email, so we can’t tell which one is right. We’ll leave every one of them untouched. Merge the duplicates in your CRM, then press Refresh.',
    },
    matchDetails: {
      label: 'What “Details” shows',
      text: 'Opens this person side by side with your CRM record, so you can choose which value to keep for any field that differs before anything is sent.',
    },
    matchDiffIntro: {
      label: 'What this comparison is',
      text: 'Where your CRM and this lead differ, choose the value you want to keep. Your choice wins over the “When a field differs” setting on the Match tab.',
    },
    matchCrmSays: {
      label: 'What “CRM says” means',
      text: 'The value your CRM holds right now. Choose it and that field is left exactly as it is.',
    },
    matchWeSay: {
      label: 'What “We say” means',
      text: 'The value we captured at the event. Choose it and it replaces what your CRM holds for that field.',
    },
    matchWillAdd: {
      label: 'What “Will add” means',
      text: 'Your CRM has nothing in this field, so we’ll fill it in. Nothing gets replaced.',
    },
    matchNotPatchable: {
      label: 'Why we can’t update this field',
      text: 'We can’t update this field on this kind of record, so we’ll leave it alone. You can still change it in your CRM yourself.',
    },
    matchEditOurs: {
      label: 'What editing here does',
      text: 'Changes what we captured for this lead in VendoIQ, not in your CRM. We look it up again right after you save, so this comparison updates.',
    },
    matchOwner: {
      label: 'Why each lead should have an owner',
      text: 'Someone in your CRM should follow up each lead. If we can’t find an owner, we don’t set one, and your CRM decides who owns the record.',
    },
    matchOwnerSuggested: {
      label: 'Why this owner was suggested',
      text: 'We matched the rep who scanned this lead to a user in your CRM, or used your default owner settings. Change it if it’s wrong. Nothing is final until you press Send.',
    },
    matchOwnerAssignedTo: {
      label: 'Why this owner was suggested from Assigned to',
      text: 'We picked this owner because the name in this lead’s “Assigned to” column matches this user in your CRM. Change it if it’s wrong. Nothing is final until you press Send.',
    },
    matchReadyTag: {
      label: 'What “Ready to send” means',
      text: 'Sending creates a new record for this person in your CRM. Nothing happens until you press Send.',
    },
    /**
     * D9 repoint (p3 Task 3): owner no longer blocks Send, so the outline
     * these two hints explain isn't "pick someone before you can send this" —
     * both replace the old, now-inaccurate `matchOwnerRequired` (which
     * claimed exactly that). They flag the two resolutions still worth a
     * second look, chosen in `OwnerPicker` by `ownerProvenance`/
     * `ownerUnverified`; a cleanly resolved owner shows neither. Also
     * replaces the Badge's bare `title` attribute, which no keyboard or
     * screen-reader user could reach.
     */
    matchOwnerIntegrationAccount: {
      label: 'No one assigned to follow up',
      text: 'We found no owner for this lead from its “Assigned to” name, the rep who scanned it, or your default owner settings, so we won’t set one. Choose an owner in the CRM owner column on the Match tab if you want someone to follow it up.',
    },
    /**
     * Same warning, for an EXISTING record that already has an owner the
     * CRM can no longer use (deactivated, or the integration account). The
     * mechanism differs from a New row's: nothing resolved, so no owner is
     * sent, and the record simply keeps the owner it has. `OwnerPicker`
     * picks this variant whenever the row carries a `currentOwner`.
     */
    matchOwnerIntegrationAccountExisting: {
      label: 'Current owner can’t follow up',
      text: 'This record’s current owner can’t follow it up: they’re deactivated in your CRM, or the owner is the account that connects your CRM to VendoIQ. We found no one else from “Assigned to”, the rep who scanned it, or your default owner settings, so the record keeps its current owner. Choose an owner in the CRM owner column on the Match tab if you want someone to follow it up.',
    },
    matchOwnerUnverified: {
      label: 'Owner couldn’t be confirmed',
      text: 'This lead uses your default owner, but we couldn’t confirm that person is still an active user who can own records in your CRM. Check them in your CRM, or choose someone else in the CRM owner column on the Match tab.',
    },
    matchBulkAssign: {
      label: 'How to assign several owners at once',
      text: 'Tick the leads you want, then choose a person here. They become the CRM owner of every ticked lead.',
    },
    sendReadyGroup: {
      label: 'Why these are already ticked',
      text: 'These leads have everything they need, so we ticked them for you. Untick any you want to hold back. They stay here for next time.',
    },
    sendNotReady: {
      label: 'Why a lead isn’t ready',
      title: 'Why a lead isn’t ready',
      lines: [
        'These leads are left out of this send. Once you fix the reason, they become ready.',
        'No email yet: Add one on the Clean tab.',
        'Multiple CRM records: Your CRM has more than one record with this email. Merge them in your CRM, then press Refresh.',
        'Notes 312/500 (for example): Your company only sends leads whose notes reach its minimum. Add to the lead’s notes, then press Refresh.',
        'Missing: Phone (for example). Your company holds leads until these fields are filled in. Fill them in, then press Refresh.',
      ],
    },
    matchPolicy: {
      label: 'What these two choices do',
      title: 'When a field differs',
      lines: [
        'Keep the CRM value: We don’t change a value your CRM already holds, unless you choose ours for a single lead. This is the safe choice, and the one we recommend.',
        'Replace it with ours: Where the two differ, what we captured at the event replaces the value in your CRM.',
        'Either way, we fill in fields your CRM has blank, and a value you choose for a single lead wins.',
        'This applies to this send only. It doesn’t change your company’s default.',
      ],
    },
    sendSummary: {
      label: 'What this summary is',
      text: 'What pressing Send will do, based on the leads ticked right now. It updates as you tick and untick leads.',
    },
    sendDisabledNoSelection: {
      label: 'Why Send is unavailable',
      text: 'Nothing is ticked. Tick at least one lead to send.',
    },
    sendDisabledReadOnly: {
      label: 'Why Send is unavailable',
      text: 'Your account can view these leads but can’t send them. Ask a company admin to send them to your CRM.',
    },
    /** One glossary for all eight outcomes — a tooltip per Tag would repeat
     *  up to eight distinct explanations down every row of the results list.
     *  The lead-ins track `outcomeLabel` above; rename one and rename both. */
    sendResults: {
      label: 'What these results mean',
      title: 'What each result means',
      lines: [
        'Created: This person wasn’t in your CRM, so we made a new record.',
        'Updated: We updated a record you already had, for example by filling in blank fields or setting its owner.',
        'Added to the event: Nothing on the record needed changing, so we only added it to this event in your CRM.',
        'Changed in your CRM: The record changed in your CRM after we looked it up, so we didn’t write over the change. Press Recheck to look again.',
        'Needs email: Still no email address, so we couldn’t look this person up.',
        'Multiple CRM records: More than one record uses this email, so we left them all alone.',
        'Failed: This lead wasn’t written. The reason is shown underneath.',
        'Held back: notes too short. This lead’s notes are below your company’s minimum, so it wasn’t sent.',
        'Held back: missing required fields. A field your company requires is blank, so it wasn’t sent.',
      ],
    },
    sendRecheck: {
      label: 'What Recheck does',
      text: 'Looks at your CRM again and rebuilds this lead’s comparison, so you can send it with the newest information.',
    },
    /**
     * Describes real traffic, so it may only be reworded alongside
     * `CrmViews`. What actually happens now: opening an event runs the match
     * check, which answers from the backend's own record of earlier look-ups
     * and reaches the CRM only for people never checked or whose email
     * changed — so a routine re-open usually costs nothing. The live read
     * (the preview: never cached, every lead with an email, every time) fires
     * when Match or Send is opened, and not before. Refresh re-runs the check,
     * and the live read too once Match or Send has been opened. Pinned by
     * "what actually reaches the CRM, and when" in `CrmViews.test.tsx`.
     *
     * That check feeds BOTH the Clean and the Match counts — it already
     * buckets the roster, so Match carries its number before anything triggers
     * the live read. Only Send's number waits for that read, because readiness
     * turns on owner data the check never fetches. Naming Clean alone here
     * would tell an operator their Match number came from somewhere it didn't.
     */
    refresh: {
      label: 'What Refresh does',
      text: 'When you open an event, we check your CRM only for leads we haven’t looked up before, or whose email changed. That gives the Clean and Match counts. The first time you open Match or Send, we look up your leads in your CRM, except leads already sent with nothing new. That takes a moment, and it fills in the Send count. Press Refresh to check again after you change something in your CRM.',
    },
    readOnly: {
      label: 'Why nothing here can be changed',
      text: 'You can look through everything here, but only a company admin can change these choices or send to your CRM.',
    },
  },
} as const;
